import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Archive, Edit3, PackagePlus, Plus, RefreshCw, Save, Trash2, TrendingUp } from 'lucide-react';
import { apiDelete, apiGet, apiPatch, apiPost } from '../../lib/api';

const inputCls = 'w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rozops-blue/20 focus:border-rozops-blue';
const emptyItem = { product_id: '', name: '', unit: '', current_stock: '', reorder_level: '' };
const emptyMovement = { inventory_item_id: '', movement_type: 'stock_in', quantity: '', reason: '' };

export default function Inventory() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [movementForm, setMovementForm] = useState(emptyMovement);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [invRes, prodRes, recRes] = await Promise.all([
        apiGet('/merchant/inventory'),
        apiGet('/merchant/products'),
        apiGet('/merchant/inventory-recommendations'),
      ]);
      setItems(invRes.data || []);
      setProducts(prodRes.data || []);
      setRecommendations(recRes.data || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => { load(); }, []);

  const resetItem = () => {
    setEditing(null);
    setItemForm(emptyItem);
    setSaving(false);
  };

  const selectProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    setItemForm({
      ...itemForm,
      product_id: productId,
      name: product ? product.name : itemForm.name,
      unit: product ? product.unit : itemForm.unit,
    });
  };

  const editItem = (item) => {
    setEditing(item);
    setItemForm({
      product_id: item.product_id || '',
      name: item.name || item.product?.name || '',
      unit: item.unit || item.product?.unit || '',
      current_stock: item.current_stock ?? '',
      reorder_level: item.reorder_level ?? '',
    });
  };

  const saveItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        product_id: itemForm.product_id || null,
        name: itemForm.name.trim(),
        unit: itemForm.unit.trim(),
        current_stock: Number(itemForm.current_stock || 0),
        reorder_level: Number(itemForm.reorder_level || 0),
      };
      if (editing) {
        await apiPatch(`/merchant/inventory/${editing.id}`, payload);
        toast.success('Inventory item updated');
      } else {
        await apiPost('/merchant/inventory', payload);
        toast.success('Inventory item added');
      }
      resetItem();
      await load();
    } catch (err) {
      toast.error(err.message);
      setSaving(false);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Deactivate ${item.name || item.product?.name}?`)) return;
    try {
      await apiDelete(`/merchant/inventory/${item.id}`);
      toast.success('Inventory item deactivated');
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const saveMovement = async (e) => {
    e.preventDefault();
    if (!movementForm.inventory_item_id) return toast.error('Select inventory item');
    setBusy(true);
    try {
      await apiPost(`/merchant/inventory/${movementForm.inventory_item_id}/movements`, {
        movement_type: movementForm.movement_type,
        quantity: Number(movementForm.quantity || 0),
        reason: movementForm.reason.trim() || null,
      });
      toast.success('Stock updated');
      setMovementForm(emptyMovement);
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const generateRecommendations = async () => {
    setBusy(true);
    try {
      const res = await apiPost('/merchant/inventory-recommendations/generate', {});
      toast.success(res.message || 'Recommendations generated');
      await load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resolveRecommendation = async (rec) => {
    try {
      await apiPost(`/merchant/inventory-recommendations/${rec.id}/resolve`, {});
      toast.success('Recommendation resolved');
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-5" data-testid="inventory-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-rozops-navy tracking-tight">Inventory</h1>
          <p className="text-sm text-rozops-text-muted mt-1">Track stock, wastage, reorder levels, and AI purchase suggestions.</p>
        </div>
        <button onClick={generateRecommendations} disabled={busy} className="bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold shadow-soft inline-flex items-center gap-2">
          <TrendingUp className="w-4 h-4" /> Generate AI suggestions
        </button>
      </div>

      <div className="grid xl:grid-cols-[1.1fr_0.9fr] gap-5">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
          <div className="flex items-center gap-2 text-rozops-accent text-sm font-bold mb-4"><PackagePlus className="w-4 h-4" /> {editing ? 'Edit item' : 'Add inventory item'}</div>
          <form onSubmit={saveItem} className="grid sm:grid-cols-2 gap-3">
            <select className={inputCls} value={itemForm.product_id} onChange={(e) => selectProduct(e.target.value)}>
              <option value="">Link product optional</option>
              {products.map(product => <option key={product.id} value={product.id}>{product.name} {product.sku ? `(${product.sku})` : ''}</option>)}
            </select>
            <input required placeholder="Item name" className={inputCls} value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} />
            <input required placeholder="Unit, e.g. litre, kg, packet" className={inputCls} value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} />
            <input type="number" min="0" step="0.01" placeholder="Current stock" className={inputCls} value={itemForm.current_stock} onChange={(e) => setItemForm({ ...itemForm, current_stock: e.target.value })} />
            <input type="number" min="0" step="0.01" placeholder="Reorder level" className={inputCls} value={itemForm.reorder_level} onChange={(e) => setItemForm({ ...itemForm, reorder_level: e.target.value })} />
            <div className="flex gap-2">
              <button type="submit" disabled={saving} className="flex-1 bg-rozops-blue text-white rounded-xl px-4 py-2.5 font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"><Save className="w-4 h-4" /> {saving ? 'Saving...' : editing ? 'Save' : 'Add'}</button>
              {editing && <button type="button" onClick={resetItem} className="px-4 py-2.5 rounded-xl text-rozops-text-muted">Cancel</button>}
            </div>
          </form>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
          <div className="flex items-center gap-2 text-rozops-accent text-sm font-bold mb-4"><RefreshCw className="w-4 h-4" /> Stock movement</div>
          <form onSubmit={saveMovement} className="space-y-3">
            <select required className={inputCls} value={movementForm.inventory_item_id} onChange={(e) => setMovementForm({ ...movementForm, inventory_item_id: e.target.value })}>
              <option value="">Select item</option>
              {items.map(item => <option key={item.id} value={item.id}>{item.name || item.product?.name} ({Number(item.current_stock || 0)} {item.unit || item.product?.unit})</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <select className={inputCls} value={movementForm.movement_type} onChange={(e) => setMovementForm({ ...movementForm, movement_type: e.target.value })}>
                <option value="stock_in">Stock in</option>
                <option value="stock_out">Stock out</option>
                <option value="wastage">Wastage</option>
                <option value="adjustment">Set stock</option>
              </select>
              <input required type="number" min="0.01" step="0.01" placeholder="Quantity" className={inputCls} value={movementForm.quantity} onChange={(e) => setMovementForm({ ...movementForm, quantity: e.target.value })} />
            </div>
            <input placeholder="Reason or note" className={inputCls} value={movementForm.reason} onChange={(e) => setMovementForm({ ...movementForm, reason: e.target.value })} />
            <button type="submit" disabled={busy} className="w-full bg-rozops-green text-white rounded-xl px-4 py-2.5 font-semibold disabled:opacity-60">Update stock</button>
          </form>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {items.length === 0 && <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center text-rozops-text-muted lg:col-span-3">No inventory items yet. Add stock items to unlock AI purchase suggestions.</div>}
        {items.map(item => {
          const stock = Number(item.current_stock || 0);
          const reorder = Number(item.reorder_level || 0);
          const low = reorder > 0 && stock <= reorder;
          return (
            <div key={item.id} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
              <div className="flex items-start justify-between gap-3">
                <div className={`w-10 h-10 rounded-xl grid place-items-center ${low ? 'bg-red-50 text-red-500' : 'bg-rozops-blue-light text-rozops-blue'}`}><Archive className="w-5 h-5" /></div>
                <div className="flex gap-1">
                  <button type="button" onClick={() => editItem(item)} className="w-9 h-9 rounded-lg text-rozops-text-muted hover:bg-gray-50 hover:text-rozops-blue grid place-items-center" title="Edit item"><Edit3 className="w-4 h-4" /></button>
                  <button type="button" onClick={() => deleteItem(item)} className="w-9 h-9 rounded-lg text-rose-500 hover:bg-rose-50 grid place-items-center" title="Deactivate item"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="mt-4 font-heading font-bold text-lg text-rozops-navy">{item.name || item.product?.name}</div>
              <div className="text-sm text-rozops-text-muted">{item.product?.sku || 'Manual item'} · {item.unit || item.product?.unit}</div>
              <div className="mt-4 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-wider text-rozops-text-muted font-bold">Current stock</div>
                  <div className="font-heading font-extrabold text-2xl text-rozops-navy">{stock.toLocaleString('en-IN')}</div>
                </div>
                <div className={`text-xs font-bold rounded-full px-2 py-1 ${low ? 'bg-red-50 text-red-600' : 'bg-rozops-green-light text-rozops-green'}`}>{low ? 'Low stock' : 'OK'}</div>
              </div>
              <div className="mt-2 text-xs text-rozops-text-muted">Reorder at {reorder.toLocaleString('en-IN')}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
        <div className="flex items-center gap-2 text-rozops-accent text-sm font-bold mb-4"><TrendingUp className="w-4 h-4" /> Open AI recommendations</div>
        {recommendations.length === 0 && <div className="text-sm text-rozops-text-muted py-6 text-center">No open recommendations. Generate AI suggestions after adding inventory.</div>}
        <div className="space-y-2">
          {recommendations.map(rec => (
            <div key={rec.id} className="grid sm:grid-cols-[1fr_auto_auto] gap-3 items-center rounded-xl border border-gray-100 p-4">
              <div>
                <div className="font-semibold text-rozops-navy">{rec.inventory_item?.name || rec.product?.name || 'Inventory item'}</div>
                <div className="text-sm text-rozops-text-muted">{rec.reason}</div>
              </div>
              <div className="font-heading font-extrabold text-rozops-green">{Number(rec.recommended_quantity || 0).toLocaleString('en-IN')}</div>
              <button type="button" onClick={() => resolveRecommendation(rec)} className="rounded-xl border border-gray-100 px-3 py-2 text-sm font-bold text-rozops-text-muted hover:text-rozops-blue">Resolve</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
